import { Box, Table, Text, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import TicketSessionByServiceLineGraph from "../charts/TicketSessionByServiceLineGraph";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";
import { formatTime, formatNumber } from "../../../store/helpers";
import {
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";

const sectionNumber = (...parts) => `${parts.filter(Boolean).join(".")}.`;
const SERVICE_NAME_COLUMN = { id: "serviceName", label: "Service", pinned: true };

const getServiceTimePerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => Number(service?.completed))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      totalServiceTime: service.sessionInSeconds,
      average: service.avg,
    }));

const getVisibleColumns = () =>
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS.filter((column) => column.id !== "serviceName");

const renderCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "completed") return <Text color="var(--blue)">{formatNumber(serviceData.completed)}</Text>;
  if (columnId === "totalServiceTime") return <Text color="var(--blue)">{formatTime(serviceData.sessionInSeconds)}</Text>;
  if (columnId === "average") return formatTime(serviceData.avg);
  if (columnId === "min") return formatTime(serviceData.min);
  if (columnId === "max") return formatTime(serviceData.max);
  return null;
};

const ServiceTimePerServiceTable = ({
  branchId = null,
  data,
  reportIndex,
  branchIndex,
  numServices,
  branchName,
}) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch, ` : "Entire Organization,";
  const branchTitle = branchId
    ? `${sectionNumber(reportIndex, branchIndex)} ${branchName} Branch`
    : "Entire Organization";
  const rows = sortReportRows(
    getServiceTimePerServiceRows({
      data,
      branchName: branchName || "",
      scope: branchId ? "Branch" : "Organization",
    }),
    config,
    { columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS }
  );
  const visibleColumns = getVisibleColumns();
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.completed) > 0)
  );

  return (
    <Box mt={10}>
      {!branchId ? (
        <Box mb="20px">
          <h2>{sectionNumber(reportIndex, 1)} Entire Organization</h2>
        </Box>
      ) : null}

      {!config.charts.enabled ? (
        <Box className="reportTableContainer serviceTimePerServiceBlock">
          {branchId ? (
            <Box className="branchServiceHeader" mb="10px">
              <h2>{branchTitle}</h2>
              <p>{numServices} services.</p>
            </Box>
          ) : null}
          <ConfiguredReportTable
            pinnedColumns={[SERVICE_NAME_COLUMN]}
            columns={visibleColumns}
            rows={rows}
            renderConfig={config}
            renderCell={renderCell}
            presortedRows={true}
          />
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => {
            const serviceTitle = `${sectionNumber(reportIndex, branchIndex, index + 1)} ${serviceData.name}`;
            return (
              <Box
                key={index}
                className={`reportContainer serviceTimePerServiceBlock${
                  branchId && index === 0 ? " branchServiceStart" : ""
                }`}
                mt="20px"
              >
                {branchId && index === 0 ? (
                  <Box className="branchServiceHeader" mb="10px">
                    <h2>{branchTitle}</h2>
                    <p>{numServices} services.</p>
                    <h3>{serviceTitle}</h3>
                    <Tag.Root>
                      <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                    </Tag.Root>
                  </Box>
                ) : (
                  <Box mb="10px">
                    <h3>{serviceTitle}</h3>
                    {branchId ? (
                      <Tag.Root>
                        <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                      </Tag.Root>
                    ) : null}
                  </Box>
                )}
                <Table.Root className="stripedTable" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader borderRight="1px solid #dadce0">Total Completed Tickets</Table.ColumnHeader>
                      <Table.ColumnHeader borderRight="1px solid #dadce0">Total Service Time</Table.ColumnHeader>
                      <Table.ColumnHeader>Avg /ticket.</Table.ColumnHeader>
                      <Table.ColumnHeader>Min.</Table.ColumnHeader>
                      <Table.ColumnHeader>Max</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell borderRight="1px solid #dadce0">{renderCell(serviceData, "completed")}</Table.Cell>
                      <Table.Cell borderRight="1px solid #dadce0">{renderCell(serviceData, "totalServiceTime")}</Table.Cell>
                      <Table.Cell>{renderCell(serviceData, "average")}</Table.Cell>
                      <Table.Cell>{renderCell(serviceData, "min")}</Table.Cell>
                      <Table.Cell>{renderCell(serviceData, "max")}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell colSpan={9} p={0}>
                        <Box mt="20px" mb="20px">
                          <TicketSessionByServiceLineGraph
                            title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
                            chart={serviceData.days}
                            chartHeight="200px"
                          />
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>
            );
          })
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default ServiceTimePerServiceTable;
